import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import {
  type AmountCurrencyRequest,
  EnumTransactionStatus,
  type StartDepositTransaction,
} from "@/api/cashier";
import {
  startTransactionMutation,
  updateTransactionMutation,
} from "@/api/cashier/@tanstack/react-query.gen";
import {
  createCreditCardPaymentAsync,
  getTokenResultAsync,
  type NuveiSafeChargeCreatePaymentRequest,
} from "@/api/safeCharge";
import type { NuveiSafeChargeGetTokenRequest } from "@/api/safeCharge/safecharge";
import { useAccount } from "@/hooks/deposit/useAccount";
import { useAmountForm } from "@/hooks/deposit/useAmountForm";
import { useMethod } from "@/hooks/deposit/useMethod";
import { useCreditCardForm } from "@/hooks/useCreditCardForm";
import { useUserData } from "@/hooks/useUserData";
import { setPaymentCorrelation } from "@/observability/datadog";
import { getMerchantConfigs, toMinorUnits } from "@/utils/helpers";

type PaymentFlowState =
  | "idle"
  | "starting-transaction"
  | "getting-card-details"
  | "update-transaction"
  | "creating-payment"
  | "completed"
  | "error";

const EXCEPTIONS_FOR_TYP = ["deposit.bin_code.blocked"];

export const useCreditCardPayment = () => {
  // Add flow state management
  const [flowState, setFlowState] = useState<PaymentFlowState>("idle");
  const [flowError, setFlowError] = useState<string | null>(null);
  const isPending =
    flowState !== "idle" && flowState !== "completed" && flowState !== "error";

  // global state & context
  const { data: userData } = useUserData();
  const { selectedAccount } = useAccount();
  const { selectedPaymentMethod, selectedSavedPaymentAccount } = useMethod();
  const { updateCreditCardFormData, creditCardFormData, creditCardFormValid } =
    useCreditCardForm();
  const { amountFormData, getAmountFormData } = useAmountForm();

  // API hooks
  const {
    mutate: startTransaction,
    data: startTransactionResponse,
    error: startTransactionError,
    reset: resetStartTransaction,
  } = useMutation({
    ...startTransactionMutation(),
    onSuccess: (data) => {
      // Correlate subsequent RUM events (incl. Nuvei SDK errors) with this transaction.
      setPaymentCorrelation({
        referenceId: data?.reference_id,
        orderId: data?.order_id,
      });
      setFlowState("getting-card-details");
      setFlowError(null);
    },
    onError: (error) => {
      setFlowState("error");
      setFlowError(
        error?.key ?? error?.message ?? "Failed to start transaction"
      );
    },
  });

  // Replaces addCardUpo — obtains a temporary cc token via the Nuvei SDK getToken call.
  const {
    mutate: getTokenResult,
    error: getTokenResultError,
    data: getTokenResultResponse,
    reset: getTokenResultReset,
  } = useMutation({
    mutationFn: ({
      scard,
      request,
    }: {
      scard: unknown;
      request: NuveiSafeChargeGetTokenRequest;
    }) => getTokenResultAsync(scard, request),
    mutationKey: ["getCreditCardToken", startTransactionResponse?.reference_id],
    onSuccess: () => {
      setFlowState("update-transaction");
      setFlowError(null);
    },
    onError: (error) => {
      setFlowState("error");
      setFlowError(error?.message ?? "Failed to get card token");
    },
  });

  const {
    mutate: updateTransaction,
    isPending: updateTransactionPending,
    reset: resetUpdateTransaction,
    error: updateTransactionErrorData,
    data: updateTransactionResponse,
  } = useMutation({
    ...updateTransactionMutation(),
    onSuccess: (data) => {
      // When the backend marks the transaction as already failed (e.g. BIN
      // blocked), skip createPayment entirely — the Nuvei session may be stale
      // and a pointless payment call would hang. Navigate straight to TYP where
      // the polled status will show "unsuccessful".
      if (data?.transaction_status === EnumTransactionStatus.EnumStatusFailed) {
        setFlowState("completed");
      } else {
        setFlowState("creating-payment");
      }
    },
    onError: (error) => {
      updateCreditCardFormData({ formValid: false });
      setFlowState("error");
      setFlowError(error?.message ?? "Failed to update transaction");
    },
  });

  const {
    mutate: createPayment,
    error: createPaymentError,
    data: createPaymentResponse,
    reset: resetCreatePayment,
  } = useMutation({
    mutationFn: createCreditCardPaymentAsync,
    mutationKey: [
      "createCreditCardPaymentAsync",
      startTransactionResponse?.reference_id,
    ],
    onSuccess: () => {
      setFlowState("completed");
      setFlowError(null);
    },
    onError: (error) => {
      setFlowState("error");
      setFlowError(error?.message ?? "Failed to create payment");
    },
  });

  // local variables
  const transactionId = startTransactionResponse?.reference_id;
  const sessionToken = startTransactionResponse?.session_token;
  const orderId = startTransactionResponse?.order_id;
  const paymentCurrencyCode = amountFormData
    ? (amountFormData?.selectedProcessingCurrency?.code ??
      selectedAccount?.currency.code)
    : undefined;

  const paymentCurrencyDecimals = amountFormData
    ? (amountFormData?.selectedProcessingCurrency?.decimals ??
      selectedAccount?.currency.decimals)
    : undefined;

  const nameOnCard =
    (selectedSavedPaymentAccount?.saved_payment_account_fields
      ?.name as string) ??
    creditCardFormData?.nameOnCard ??
    "";

  // Reset function to clear all states and start over
  const resetPaymentFlow = useCallback(() => {
    setFlowState("idle");
    setFlowError(null);
    resetStartTransaction();
    resetUpdateTransaction();
    resetCreatePayment();
    getTokenResultReset();
  }, [
    resetStartTransaction,
    resetUpdateTransaction,
    resetCreatePayment,
    getTokenResultReset,
  ]);

  // Main entry function - only starts if in idle state
  const creditCardPayment = useCallback(() => {
    // A payment is already in flight (e.g. a slow/hung PSP tokenisation call) —
    // ignore the re-entrant call instead of forcing an error state. Treating this
    // the same as a validation failure below would flip `error` to truthy and,
    // since `transactionId` is already set from the in-flight attempt, would
    // immediately satisfy `shouldContinueToTYP` and redirect to the status page
    // while the original request is still pending.
    if (isPending) {
      return;
    }

    // Read the latest amount from the store, not a stale render closure, so the
    // started transaction matches what the submit guard validated.
    const liveAmountFormData = getAmountFormData();

    if (
      !userData ||
      !selectedPaymentMethod ||
      !liveAmountFormData ||
      !selectedAccount ||
      !(nameOnCard || selectedSavedPaymentAccount)
    ) {
      setFlowState("error");
      setFlowError("deposit.incomplete_field");
      throw new Error("Can start credit card flow");
    }

    const payload: StartDepositTransaction = {
      payment_method_id: selectedPaymentMethod.id,
      trading_account_id: selectedAccount.id,
      trading_amount_with_currency: {
        amount: String(
          toMinorUnits(
            liveAmountFormData.tradingValue,
            selectedAccount.currency.decimals
          )
        ),
        code: selectedAccount.currency.code,
      } as AmountCurrencyRequest,
      saved_payment_account_id: selectedSavedPaymentAccount?.id,
      validate_spa_fields: false,
    };

    setFlowState("starting-transaction");
    startTransaction({ body: payload });
  }, [
    isPending,
    userData,
    selectedPaymentMethod,
    getAmountFormData,
    selectedAccount,
    nameOnCard,
    startTransaction,
    selectedSavedPaymentAccount,
  ]);

  // Chain: Get card token (new card) or skip directly to update-transaction (existing SPA)
  useEffect(() => {
    if (flowState !== "getting-card-details") return;

    // Existing saved card — no need to tokenise, proceed directly to update-transaction where
    // the backend will look up the SPA and verify the BIN.
    if (selectedSavedPaymentAccount) {
      setFlowState("update-transaction");
      return;
    }

    if (
      !nameOnCard ||
      !startTransactionResponse ||
      !startTransactionResponse.session_token ||
      !startTransactionResponse.order_id
    ) {
      return;
    }

    // New card — use getToken to get a temporary cc token for the backend HandleUPO call.
    getTokenResult({
      scard: window.scard,
      request: {
        sessionToken: startTransactionResponse.session_token,
        cardHolderName: nameOnCard,
        ...getMerchantConfigs(startTransactionResponse),
      },
    });
  }, [
    flowState,
    startTransactionResponse,
    selectedSavedPaymentAccount,
    nameOnCard,
    getTokenResult,
  ]);

  // Handle update-transaction: send cc_temp_token (new card) or SPA ID (existing SPA)
  // State transitions on success/failure are handled by the mutation's onSuccess/onError callbacks.
  useEffect(() => {
    if (flowState !== "update-transaction" || !transactionId) return;

    if (!selectedAccount || !sessionToken || !orderId || getTokenResultError) {
      updateCreditCardFormData({ formValid: false });
      setFlowState("error");
      setFlowError("Invalid card details");
      return;
    }

    if (updateTransactionPending) {
      return;
    }

    if (paymentCurrencyCode && paymentCurrencyDecimals) {
      if (selectedSavedPaymentAccount) {
        // Existing SPA — send the SPA ID; backend fetches fields and checks BIN.
        updateTransaction({
          path: { referenceID: transactionId },
          body: {
            saved_payment_account_id: selectedSavedPaymentAccount.id,
          },
        });
      } else if (getTokenResultResponse?.ccTempToken) {
        // New card — send the temporary cc token; backend calls HandleUPO to register the card.
        updateTransaction({
          path: { referenceID: transactionId },
          body: {
            cc_temp_token: getTokenResultResponse.ccTempToken,
          },
        });
      } else if (
        getTokenResultResponse &&
        !getTokenResultResponse.ccTempToken
      ) {
        // SDK resolved but didn't return a temp token — surface an error instead of stalling.
        updateCreditCardFormData({ formValid: false });
        setFlowState("error");
        setFlowError("Card tokenisation failed: no temporary token received");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    flowState,
    getTokenResultError,
    getTokenResultResponse,
    transactionId,
    selectedPaymentMethod,
    updateTransaction,
    updateCreditCardFormData,
    amountFormData.processingValue,
    amountFormData.rate,
    amountFormData.tradingValue,
    orderId,
    paymentCurrencyCode,
    paymentCurrencyDecimals,
    selectedAccount,
    sessionToken,
    nameOnCard,
    selectedSavedPaymentAccount,
  ]);

  // Chain: Create payment after update-transaction succeeded
  useEffect(() => {
    if (
      flowState !== "creating-payment" ||
      !orderId ||
      !sessionToken ||
      !startTransactionResponse.order_id ||
      !userData ||
      !transactionId ||
      !(nameOnCard || selectedSavedPaymentAccount)
    ) {
      return;
    }

    // For new-card flow the UPO ID comes back in the update-transaction response so that Nuvei
    // can link the createPayment call to the registered UPO.
    // For existing-SPA the payment_token (permanent UPO) is used directly.
    const upoID =
      updateTransactionResponse?.user_payment_option_id ??
      selectedSavedPaymentAccount?.payment_token;

    const payload: NuveiSafeChargeCreatePaymentRequest = {
      clientUniqueId: transactionId,
      cardHolderName: nameOnCard,
      userTokenId: userData.sca_id,
      sessionToken,
      savedPaymentAccountPspToken: selectedSavedPaymentAccount?.payment_token,
      userPaymentOptionId: upoID,
      ...getMerchantConfigs(startTransactionResponse),
    };

    createPayment(payload);
  }, [
    createPayment,
    flowState,
    orderId,
    sessionToken,
    nameOnCard,
    transactionId,
    userData,
    selectedSavedPaymentAccount,
    updateTransactionResponse,
    startTransactionResponse,
  ]);

  // calculated return values

  const error =
    startTransactionError?.key ??
    updateTransactionErrorData?.key ??
    startTransactionError?.message ??
    getTokenResultError?.message ??
    createPaymentError?.message ??
    flowError;

  const result =
    flowState === "completed" ? createPaymentResponse?.result : undefined;

  const shouldContinueToTYP =
    transactionId &&
    (flowState === "completed" || result || error) &&
    !EXCEPTIONS_FOR_TYP.includes(error || "");

  return {
    creditCardPayment,
    result,
    error,
    isPending,
    transactionId,
    flowState,
    resetPaymentFlow,
    // detailed data for debugging
    startTransactionResponse,
    createPaymentResponse,
    getTokenResultResponse,
    createPaymentError,
    getTokenResultError,
    creditCardFormValid,
    sessionToken,
    orderId,
    shouldContinueToTYP,
    isRestrictedBinCode: error === EXCEPTIONS_FOR_TYP[0],
  };
};
